/**
 * Extraction runs and engine drafts. Staff see status, the patient, the model or rule set that
 * produced the job, and the error when one failed. A failed file can be queued to be read again.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminDraftJob, AdminExtractionJob, AdminJobPatient, AdminJobs } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { Me } from "./api.ts";
import { api } from "./api.ts";
import { RecordLink, Truncate } from "./bits.tsx";
import type { Column } from "./layout.tsx";
import { FilterChip, FilterRow, PageHead, Panel, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { href } from "./router.ts";
import { Button, ErrorText, Status } from "./ui.tsx";
import { moment, refusal } from "./words.ts";
const EXTRACTION_STATUS = {
  running: <Status tone="warn">Running</Status>,
  succeeded: <Status tone="ok">Succeeded</Status>,
  failed: <Status tone="danger">Failed</Status>
};
const DRAFT_STATUS = {
  draft: <Status tone="neutral">Draft</Status>,
  in_review: <Status tone="warn">In review</Status>,
  approved: <Status tone="ok">Approved</Status>,
  declined: <Status tone="danger">Declined</Status>,
  superseded: <Status tone="neutral">Superseded</Status>
};
function canRetry(role: Me["staffRole"]): boolean {
  return role === "superuser" || role === "operations" || role === "support";
}
function patientLink(patient: AdminJobPatient) {
  const label = patient.name ?? patient.email ?? "Name not given";
  return <RecordLink to={href.patient(patient.id)} relation="Patient" label={label} />;
}
function RetryPanel({
  job,
  onDone,
  onCancel
}: {
  readonly job: AdminExtractionJob;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    panel.current?.scrollIntoView({
      block: "nearest"
    });
    panel.current?.querySelector<HTMLElement>("textarea")?.focus({
      preventScroll: true
    });
  }, []);
  async function submit(reason: string): Promise<string | null> {
    const result = await api<{
      readonly queued: boolean;
    }>(ROUTES.adminRetryExtraction, {
      params: {
        runId: job.id
      },
      body: {
        reason
      }
    });
    if (!result.ok) return refusal(result.error);
    onDone();
    return null;
  }
  return <div ref={panel}>
      <Panel padded>
        <div className="x78zum5 xdt5ytf x1r0x45p x27kpxv">
          <h2 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">Read {job.filename} again</h2>
          <p className="x1ghz6dp x1jty25b">
            The file is queued to be read again. A new run is recorded when reading starts.
          </p>
          <ReasonForm id="retry-extraction-reason" hint="Logged with your name. Include the ticket number." action="Read this file again" onSubmit={submit} onCancel={onCancel} />
        </div>
      </Panel>
    </div>;
}
function extractionColumns(retry: ((job: AdminExtractionJob) => void) | null): readonly Column<AdminExtractionJob>[] {
  const columns: Column<AdminExtractionJob>[] = [{
    label: "File",
    cell: job => <Truncate value={job.filename} />
  }, {
    label: "Patient",
    cell: job => patientLink(job.patient)
  }, {
    label: "Status",
    nowrap: true,
    cell: job => EXTRACTION_STATUS[job.status]
  }, {
    label: "Error",
    wide: true,
    cell: job => job.error === null ? <span className="x1jty25b">None</span> : job.error
  }, {
    label: "Model",
    phoneLabel: true,
    cell: job => job.model
  }, {
    label: "Prompt",
    phoneLabel: true,
    nowrap: true,
    cell: job => job.promptVersion
  }, {
    label: "Markers",
    numeric: true,
    phoneLabel: true,
    cell: job => job.observationCount
  }, {
    label: "Started",
    numeric: true,
    phoneLabel: true,
    cell: job => moment(job.startedAt)
  }];
  if (retry !== null) {
    columns.push({
      label: "Actions",
      wide: true,
      cell: job => job.retryable ? <Button tone="quiet" onClick={() => retry(job)}>
            Read again
          </Button> : null
    });
  }
  return columns;
}
const DRAFT_COLUMNS: readonly Column<AdminDraftJob>[] = [{
  label: "Patient",
  cell: job => patientLink(job.patient)
}, {
  label: "Status",
  nowrap: true,
  cell: job => DRAFT_STATUS[job.status]
}, {
  label: "Version",
  numeric: true,
  phoneLabel: true,
  cell: job => job.version
}, {
  label: "Engine",
  phoneLabel: true,
  nowrap: true,
  cell: job => job.engineVersion
}, {
  label: "Rule set",
  phoneLabel: true,
  nowrap: true,
  cell: job => job.ruleSetVersion
}, {
  label: "Ingredients",
  numeric: true,
  phoneLabel: true,
  cell: job => job.componentCount
}, {
  label: "Labs in",
  numeric: true,
  phoneLabel: true,
  cell: job => job.observationCount
}, {
  label: "Answers in",
  numeric: true,
  phoneLabel: true,
  cell: job => job.intakeCount
}, {
  label: "Drafted",
  numeric: true,
  phoneLabel: true,
  cell: job => moment(job.createdAt)
}];
function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}
const PAGE = 50;
function pageOf<T>(rows: readonly T[], page: number): readonly T[] {
  return rows.slice(page * PAGE, page * PAGE + PAGE);
}
function StatusFilters({
  view,
  status,
  onPick
}: {
  readonly view: "extractions" | "drafts";
  readonly status: string;
  readonly onPick: (status: string) => void;
}) {
  const options: readonly (readonly [string, string])[] = view === "extractions" ? [["all", "All"], ["failed", "Failed"], ["running", "Running"]] : [["all", "All"], ["draft", "Draft"], ["in_review", "In review"]];
  return <FilterRow>
      {options.map(([id, label]) => <FilterChip key={id} to={href.jobs(view)} label={label} current={status === id} onClick={() => onPick(id)} />)}
    </FilterRow>;
}
function Loaded({
  me,
  jobs,
  view,
  onReload
}: {
  readonly me: Me;
  readonly jobs: AdminJobs;
  readonly view: "extractions" | "drafts";
  readonly onReload: () => void;
}) {
  const [retrying, setRetrying] = useState<AdminExtractionJob | null>(null);
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  useEffect(() => {
    setStatus("all");
    setPage(0);
  }, [view]);
  const retry = canRetry(me.staffRole) && jobs.extractions.some(job => job.retryable) ? setRetrying : null;
  const extractions = status === "all" ? jobs.extractions : jobs.extractions.filter(job => job.status === status);
  const drafts = status === "all" ? jobs.drafts : jobs.drafts.filter(job => job.status === status);
  const total = view === "extractions" ? extractions.length : drafts.length;
  const from = total === 0 ? 0 : page * PAGE + 1;
  const to = Math.min(total, (page + 1) * PAGE);
  return <>
      <PageHead title="Jobs" lead={view === "extractions" ? "Files being read. A draft is a separate list." : "Formulas the engine drafted. Extraction runs are a separate list."} meta={`${jobs.config.extractionModel} · prompt ${jobs.config.promptVersion} · engine ${jobs.config.engineVersion} · rules ${jobs.config.ruleSetVersion}`} />
      <FilterRow>
        <FilterChip to={href.jobs("extractions")} label="Extractions" count={jobs.extractions.length} current={view === "extractions"} />
        <FilterChip to={href.jobs("drafts")} label="Drafts" count={jobs.drafts.length} current={view === "drafts"} />
      </FilterRow>
      <StatusFilters view={view} status={status} onPick={next => {
      setStatus(next);
      setPage(0);
    }} />
      {view === "extractions" ? <Table label="Extraction runs" columns={extractionColumns(retry)} rows={pageOf(extractions, page)} rowKey={job => job.id} empty={jobs.extractions.length === 0 ? "No files have been read yet. An upload creates a run." : "No runs match this status."} selected={job => job.id === retrying?.id} /> : <Table label="Formula drafts" columns={DRAFT_COLUMNS} rows={pageOf(drafts, page)} rowKey={job => job.id} empty={jobs.drafts.length === 0 ? "No formulas have been drafted yet. A draft appears after labs and intake." : "No drafts match this status."} />}
      <p className="x1ghz6dp x1jty25b">
        {plural(total, "row", "rows")}. Showing {from}–{to}.
        {page > 0 ? <Button tone="quiet" onClick={() => setPage(n => n - 1)}>
            Previous
          </Button> : null}
        {to < total ? <Button tone="quiet" onClick={() => setPage(n => n + 1)}>
            Next
          </Button> : null}
      </p>
      {retrying === null ? null : <RetryPanel job={retrying} onDone={onReload} onCancel={() => setRetrying(null)} />}
    </>;
}
export function Jobs({
  me,
  view
}: {
  readonly me: Me;
  readonly view: "extractions" | "drafts";
}) {
  const [nonce, setNonce] = useState(0);
  const [found] = useLoad(`jobs-${nonce}`, () => api<AdminJobs>(ROUTES.adminJobs));
  if (found.state === "loading") return <PageHead title="Jobs" meta="Loading" />;
  if (found.state === "failed") return <ErrorText>{refusal(found.error)}</ErrorText>;
  return <Loaded me={me} view={view} jobs={found.data} onReload={() => setNonce(n => n + 1)} />;
}